import classNames from "classnames";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { formatCurrencyValue } from "shared/locale/format";
import { CurrencyCode } from "shared/types";

import { hasUsableRange } from "./utils";

const PriceTitle = ({
  minimum,
  maximum,
  quoted,
  currencyCode,
  aiEstimate,
  laborQuantity,
  showCurrencyForEstimate = false,
  hasExpandableContent = false,
  onToggleExpand,
  isExpanded = false,
}: {
  minimum: number;
  maximum: number;
  quoted: number;
  currencyCode: CurrencyCode;
  aiEstimate?: string | null;
  laborQuantity?: number | null;
  showCurrencyForEstimate?: boolean;
  hasExpandableContent?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}) => {
  const { t } = useTranslation("duck");

  if (quoted === undefined || quoted === null) {
    return <div>{t("no_quote_available")}</div>;
  }

  const rangeIsUsable = hasUsableRange(minimum, maximum);

  let quotePercentage;

  if (!rangeIsUsable) {
    quotePercentage = 0.5;
  } else {
    quotePercentage = Math.max(0, (quoted - minimum) / (maximum - minimum));
  }

  return (
    <div className="flex justify-center relative">
      <div className="w-100 relative max-w-64 text-xs py-2 min-h-7">
        <div className="absolute" style={{ left: `${quotePercentage * 100}%` }}>
          <div
            className="absolute h-4 w-0.5 bg-red-500"
            style={{ top: "-12px" }}
          />
          <div
            className="flex mt-[-12px]"
            style={getContainerPosition(quotePercentage)}
          >
            <div
              className="whitespace-nowrap"
              style={getQuotedPosition(quotePercentage)}
            >
              <div>{t("quoted")}</div>
            </div>
            <div
              className="font-semibold"
              style={getPricePosition(quotePercentage)}
            >
              {formatCurrencyValue(currencyCode || "USD", quoted)}
              {laborQuantity !== null &&
                laborQuantity !== undefined &&
                laborQuantity > 0 && (
                  <span className="text-neutral-500 font-normal ml-1">
                    ({laborQuantity}h)
                  </span>
                )}
            </div>
          </div>
          {aiEstimate !== null && aiEstimate !== undefined && (
            <div
              className="text-neutral-500 absolute text-nowrap top-[4px]"
              style={getAIEstimatePosition(quotePercentage)}
            >
              {t("ai_estimate", { estimate: aiEstimate })}
              {showCurrencyForEstimate && ` ${currencyCode || "USD"}`}
            </div>
          )}
        </div>
      </div>
      {hasExpandableContent && onToggleExpand && (
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex items-center justify-center hover:bg-gray-50 rounded p-1 transition-colors absolute right-[0px] top-[-5px]"
          aria-expanded={isExpanded}
        >
          <ExpandMoreIcon
            fontSize="small"
            className={classNames("transition-transform duration-200", {
              "rotate-180": isExpanded,
            })}
          />
        </button>
      )}
    </div>
  );
};

const getAIEstimatePosition = (
  quotePercentage: number
): React.CSSProperties => {
  if (quotePercentage < 0.2) {
    return { left: "5px", position: "absolute" };
  } else if (quotePercentage > 0.75) {
    return { right: "5px", position: "absolute" };
  } else {
    return {
      left: "50%",
      transform: "translateX(-50%)",
      position: "absolute",
    };
  }
};

const getContainerPosition = (quotePercentage: number): React.CSSProperties => {
  if (quotePercentage < 0.2) {
    return { left: "5px", position: "absolute" };
  } else if (quotePercentage > 0.75) {
    return { right: "5px", position: "absolute" };
  } else {
    return {};
  }
};

// If the quoted value is in the middle, we draw the line in the middle, if it is too low, we draw both Quoted and price on the right side
// If the quoted value is too high, we draw both Quoted and price on the left side
const getQuotedPosition = (quotePercentage: number): React.CSSProperties => {
  if (quotePercentage < 0.2) {
    return {};
  } else if (quotePercentage > 0.75) {
    return { marginRight: "5px" };
  } else {
    return {
      right: "5px",
      position: "absolute",
    };
  }
};

const getPricePosition = (quotePercentage: number): React.CSSProperties => {
  if (quotePercentage < 0.2) {
    return { marginLeft: "5px" };
  } else if (quotePercentage > 0.75) {
    return { right: "5px" };
  } else {
    return {
      left: "5px",
      position: "absolute",
    };
  }
};

export default PriceTitle;
